import { isDefinedString, isPositiveNumber } from "et3-type-guards";
import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";
import { apiCallGeneratePromoCode } from "../../../api/communications-api/api-call-generate-promocode";
import { getSpecificDateWithDays } from "../../../core/et3-date";
import { isValidReferralOrPromoCodeCode } from "../../../helpers/et3-data-helper";
import getPromoCodeStore from "../../auditor-store/promocode-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import { isStartDateBeforeExpiry } from "../../../helpers/et3-date-helper";
import { MAX_PROMOCODE_GENERATION_COUNT } from "../../../constants/input-entries-constants";

const PROMOCODE_KEYS_SEPARATOR = "\n";

class promoCodeGenerationStore {
  generateButtonLoading = observable.box<boolean>(false);
  promoCodeRewardType = observable.box<TEt3PickerSingleValue>({
    value: "",
    label: "Reward Type"
  });
  promoCodeDescripion = observable.box<string>("");
  promoCodeKeys = observable.box<string[]>([]);
  promoCodeRewardInHours = observable.box<number>(0);
  promoCodeMaxUsage = observable.box<number>(0);
  numberOfPromoCodes = observable.box<number>(1);
  startDate = observable.box<Date>(new Date());
  expirationDate = observable.box<Date>(
    getSpecificDateWithDays({
      daysSubtracted: 0,
      daysAdded: 100
    })
  );

  setPromoCodeDescripion = (value: string) => {
    runInAction(() => {
      this.promoCodeDescripion.set(value);
    });
  };

  promoCodeForGender = observable.box<TEt3PickerSingleValue>({
    value: "ALL",
    label: "All"
  });
  promoCodeForNationality = observable.box<TEt3PickerSingleValue>({
    label: "All",
    value: "ALL"
  });
  promoCodeIsActive = observable.box<TEt3PickerSingleValue>({
    value: "1",
    label: "Active"
  });
  promoCodeForPaid = observable.box<TEt3PickerSingleValue>({
    label: "All",
    value: "-1"
  });

  generateButtonDisabled = computed(() => {
    return (
      this.generateButtonLoading.get() ||
      !isDefinedString(this.promoCodeDescripion.get().trim()) ||
      !isPositiveNumber(this.promoCodeRewardInHours.get()) ||
      !isPositiveNumber(this.promoCodeMaxUsage.get()) ||
      !isDefinedString(this.promoCodeRewardType.get().value) ||
      !isPositiveNumber(this.numberOfPromoCodes.get()) ||
      this.numberOfPromoCodes.get() > MAX_PROMOCODE_GENERATION_COUNT
    );
  });

  resetPromoCode = () => {
    const hundredDaysAdded = getSpecificDateWithDays({
      daysSubtracted: 0,
      daysAdded: 100
    });
    runInAction(() => {
      this.expirationDate.set(hundredDaysAdded);
      this.promoCodeDescripion.set("");
      this.promoCodeKeys.set([]);
      this.promoCodeRewardInHours.set(0);
      this.numberOfPromoCodes.set(1);
      this.promoCodeRewardType.set({
        label: "Reward Type",
        value: ""
      });
      this.promoCodeMaxUsage.set(0);
      this.promoCodeForGender.set({
        label: "All",
        value: "ALL"
      });
      this.promoCodeForNationality.set({
        label: "All",
        value: "ALL"
      });
      this.promoCodeIsActive.set({
        value: "1",
        label: "Active"
      });
      this.promoCodeForPaid.set({ label: "All", value: "-1" });
      getPromoCodeStore().allPromocodesLimit.set({
        label: "10",
        value: "10"
      });
    });
  };
  setPromoCodeGenerateLoading = (loading: boolean) => {
    runInAction(() => {
      this.generateButtonLoading.set(loading);
    });
  };

  setPromoCodeKeys = (values: string[]) => {
    const areAllPromoCodesAccepted = values.every((value) =>
      isValidReferralOrPromoCodeCode(value)
    );
    if (areAllPromoCodesAccepted) {
      runInAction(() => {
        this.promoCodeKeys.set(values);
        getCorePopUpStore().alertAction.set("COPY");
      });
    }
  };

  setNumberOfPromoCodes = (value: number) => {
    runInAction(() => {
      this.numberOfPromoCodes.set(value);
    });
  };

  setPromoCodeIsActive = (status: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.promoCodeIsActive.set(status);
    });
  };

  setPromoCodeRewardType = (rewardType: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.promoCodeRewardType.set(rewardType);
    });
  };
  setPromoCodeRewardInHours = (rewardInHours: number) => {
    runInAction(() => {
      this.promoCodeRewardInHours.set(rewardInHours);
    });
  };

  setPromoCodeForNationality = (nationality: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.promoCodeForNationality.set(nationality);
    });
  };

  setPromoCodeForGender = (gender: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.promoCodeForGender.set(gender);
    });
  };

  setPromoCodeForPaid = (paid: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.promoCodeForPaid.set(paid);
    });
  };

  generatePromoCode = async () => {
    if (
      !isStartDateBeforeExpiry(this.startDate.get(), this.expirationDate.get())
    ) {
      return;
    }

    this.setPromoCodeGenerateLoading(true);
    const promoCodeForPaid = parseInt(this.promoCodeForPaid.get().value, 10);
    const promoCodeIsActive = parseInt(this.promoCodeIsActive.get().value, 10);
    getCoreUIStore().setIsLoading("COMMUNICATIONS_PROMO_CODE_GENERATOR", true);
    const { data, status, code } = await apiCallGeneratePromoCode({
      description: this.promoCodeDescripion.get().trim(),
      benefitInHours: this.promoCodeRewardInHours.get(),
      benefitType: this.promoCodeRewardType.get().value,
      maxUsage: this.promoCodeMaxUsage.get(),
      promoCodeForGender: this.promoCodeForGender.get().value,
      promoCodeForNationality: this.promoCodeForNationality.get().value,
      isActive: promoCodeIsActive,
      startTime: this.startDate.get(),
      expiryTime: this.expirationDate.get(),
      promoCodeForPaid: promoCodeForPaid,
      numberOfCodes: this.numberOfPromoCodes.get()
    });
    getCoreUIStore().setIsLoading("COMMUNICATIONS_PROMO_CODE_GENERATOR", false);
    if (status === "OK") {
      this.resetPromoCode();
      this.setPromoCodeKeys(data);
      await getPromoCodeStore().getAllPromocodes();
      getCorePopUpStore().showSuccessMessageWithExtraData(
        "PROMO_CODE_GENERATED_SUCCESSFULLY",
        this.promoCodeKeys.get().join(PROMOCODE_KEYS_SEPARATOR),
        false
      );
      this.setPromoCodeGenerateLoading(false);
    } else {
      getCorePopUpStore().showAlertMessage(code);
      this.setPromoCodeGenerateLoading(false);
    }
  };
}

const getPromoCodeGenerationStore = memoize(
  () => new promoCodeGenerationStore(),
  () => 1
);
export default getPromoCodeGenerationStore;
